import type { WorklistIssue } from "../../studio/cellState"

/**
 * Studio V2's gate predicate: a Verordnung is "geprüft" exactly when the
 * OVERRIDE-ADJUSTED worklist holds no issue for it.
 *
 * THE ONE SOURCE OF TRUTH. Both existing gates read this same list — Mission
 * Control's `canFinish` is `worklist.length === 0`, and the server's `approve()`
 * re-derives it via `apply_overrides(derive_worklist(ctx), …)` before its 409.
 * Studio V2's per-framework checkmark is that list FILTERED, never a local
 * click-flag: a checkbox the derivation does not read would drift from the gate
 * the moment an edit reopens an issue (or an override closes one).
 *
 * Callers MUST pass `useDeclarationFlowState().worklist` (override-adjusted) —
 * not `rawWorklist`, which exists for parity comparability only.
 */
export function frameworkGeprueft(
  framework: string,
  worklist: readonly WorklistIssue[],
): boolean {
  return !worklist.some((i) => i.framework === framework)
}

/** The declaration-level gate — byte-equivalent to Mission Control's
 *  `canFinish` condition on the same list, EXCEPT it also requires at least
 *  one framework to exist. `worklist.length === 0` alone is vacuously true
 *  for a declaration with no Verordnung extracted at all (nothing to derive
 *  an issue FROM), which used to read as "everything checked" — the honest
 *  reading of "nothing was extracted yet" is not-geprüft, never approvable. */
export function declarationGeprueft(
  worklist: readonly WorklistIssue[],
  frameworks: readonly string[],
): boolean {
  return frameworks.length > 0 && worklist.length === 0
}

/** Open issues grouped per framework, for the navigator's row states and the
 *  footer's "noch X von N zu prüfen" count. Preserves worklist order. */
export function openWorkByFramework(
  worklist: readonly WorklistIssue[],
): Map<string, WorklistIssue[]> {
  const byFramework = new Map<string, WorklistIssue[]>()
  for (const issue of worklist) {
    const list = byFramework.get(issue.framework)
    if (list) list.push(issue)
    else byFramework.set(issue.framework, [issue])
  }
  return byFramework
}
